import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Lenis from 'lenis';
import { Toaster, toast } from 'sonner';
import './services/notificationService';
import ErrorBoundary from './components/ErrorBoundary';
import PanelErrorBoundary from './components/PanelErrorBoundary';
import Header from './components/Header';
import ScrudixLogo from './components/ScrudixLogo';
import { defaultPageMeta, pageMetaByView, type PageMeta } from './content/pageMeta';
import { footerInfoCards } from './content/siteContent';
import apiClient from './services/apiClient';
import { syncAnalyticsWithBackend, trackEvent, trackPageView } from './services/analytics';
import { viewToPath, resolveView, getBlogSlug, getComparisonSlug } from './services/router';
import { lazyWithRetry } from './services/lazyWithRetry';
import { useAuthStore, type UserData } from './services/authStore';
import { useUIStore } from './services/uiStore';
import { useTheme } from './services/ThemeContext';
import { View } from './types';
import { getComparisonPage } from './content/comparisonPages';
import MaintenancePage from './components/MaintenancePage';
import TeamDashboardPage from './components/TeamDashboardPage';
import ReminderBanner from './components/ReminderBanner';
import ReminderAlerts from './components/ReminderAlerts';
import TransparentMascotCharacter from './components/TransparentMascotCharacter';

const scrudixDarkInterfaceVideoSrc = '/video/scrudixdarkinterface.mp4';

const Hero = lazyWithRetry(() => import('./components/Hero'));
const AIBuilder = lazyWithRetry(() => import('./components/AIBuilder'));
const Auth = lazyWithRetry(() => import('./components/Auth'));
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const Pricing = lazyWithRetry(() => import('./components/Pricing'));
const ExtensionsPage = lazyWithRetry(() => import('./components/ExtensionsPage'));
const Documentation = lazyWithRetry(() => import('./components/Documentation'));
const DemoShowcase = lazyWithRetry(() => import('./components/DemoShowcase'));
const LegalPages = lazyWithRetry(() => import('./components/LegalPages'));
const CommandsPage = lazyWithRetry(() => import('./components/CommandsPage'));
const LumiExperience = lazyWithRetry(() => import('./components/LumiExperience'));
const HiggsfieldExperience = lazyWithRetry(() => import('./components/HiggsfieldStudio'));
const FeaturesPage = lazyWithRetry(() => import('./components/FeaturesPage'));
const HowItWorksPage = lazyWithRetry(() => import('./components/HowItWorksPage'));
const WorkClockDevPage = lazyWithRetry(() => import('./components/WorkClockDevPage'));
const ScrudixStudioAIStudio = lazyWithRetry(() => import('./components/ScrudixStudioAIStudio'));
const NotFoundPage = lazyWithRetry(() => import('./components/NotFoundPage'));
const BlogPage = lazyWithRetry(() => import('./components/BlogPage'));
const BlogPost = lazyWithRetry(() => import('./components/BlogPost'));
const ComparisonPage = lazyWithRetry(() => import('./components/ComparisonPage'));
const CommunityPage = lazyWithRetry(() => import('./components/CommunityPage'));
const LeetCodeTutorArena = lazyWithRetry(() =>
  import('./components/LeetCodeTutorArena').then((m) => ({ default: m.LeetCodeTutorArena }))
);


// UserData type imported from authStore

const loadingHighlights = ['AI apps', 'dashboards', 'APIs', 'debugging flows'];
const loadingCapabilities = ['Prompt Understanding', 'UI Generation', 'Backend Logic', 'Docs + QA'];

const RouteLoadingFallback: React.FC = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="rounded-2xl border border-zinc-200 bg-white/80 px-6 py-4 text-sm font-bold text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300">
      Loading...
    </div>
  </div>
);

/** Read a persisted "fresh login" grace timestamp so we trust a new session
 *  across full page reloads (OAuth redirects, etc.). */
function readFreshLoginGrace(key: string): number {
  try {
    const raw = sessionStorage.getItem(key);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > Date.now() ? n : 0;
  } catch {
    return 0;
  }
}

const ThemeVideoBackdrop: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.visibilityState === 'visible') {
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-black" aria-hidden="true">
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full scale-[1.02] object-cover object-center will-change-transform translate-z-0"
      >
        <source src={scrudixDarkInterfaceVideoSrc} type="video/mp4" />
      </video>
    </div>
  );
};

const upsertMetaByName = (name: string, content: string) => {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertMetaByProperty = (property: string, content: string) => {
  let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const applyPageMeta = (meta: PageMeta, options?: { indexable?: boolean }) => {
  const socialImage = 'https://scrudix.com/scrudix-logo-current.png';
  const indexable = options?.indexable ?? true;

  document.title = meta.title;
  upsertMetaByName('description', meta.description);
  upsertMetaByName(
    'robots',
    indexable
      ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      : 'noindex, nofollow, noarchive'
  );
  upsertMetaByName('googlebot', indexable ? 'index, follow' : 'noindex, nofollow');
  if (meta.keywords) {
    upsertMetaByName('keywords', meta.keywords);
  } else {
    const existingKeywords = document.querySelector('meta[name="keywords"]');
    if (existingKeywords) { existingKeywords.remove(); }
  }
  upsertMetaByProperty('og:title', meta.title);
  upsertMetaByProperty('og:description', meta.description);
  upsertMetaByProperty('og:type', 'website');
  upsertMetaByProperty('og:image', socialImage);
  upsertMetaByProperty('og:image:alt', meta.title);
  upsertMetaByName('twitter:title', meta.title);
  upsertMetaByName('twitter:description', meta.description);
  upsertMetaByName('twitter:card', 'summary_large_image');
  upsertMetaByName('twitter:image', socialImage);
  upsertMetaByName('twitter:image:alt', meta.title);

  // Update canonical URL per route
  const normalizedPath = window.location.pathname === '/'
    ? '/'
    : window.location.pathname.replace(/\/+$/, '');
  const canonical = `https://scrudix.com${normalizedPath}`;
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', canonical);
  upsertMetaByProperty('og:url', canonical);
  upsertMetaByName('twitter:url', canonical);
};

/**
 * Maintenance mode check.
 * Priority: 1) admin bypass  2) localStorage override  3) env var  4) backend /api/maintenance/status
 *
 * Admin bypass (persists across tabs & refreshes):
 *   Visit: yoursite.com?bypass_maintenance=YOUR_SECRET
 *   Clear: yoursite.com?clear_bypass=1  OR  localStorage.removeItem('scrudix_maintenance_bypass')
 */
const BYPASS_STORAGE_KEY = 'scrudix_maintenance_bypass';

const isAdminBypassed = (): boolean => {
  // Check URL query param first
  const params = new URLSearchParams(window.location.search);

  // Allow clearing bypass
  if (params.get('clear_bypass') === '1') {
    localStorage.removeItem(BYPASS_STORAGE_KEY);
    // Clean URL
    params.delete('clear_bypass');
    const clean = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}${window.location.hash}`;
    window.history.replaceState(null, '', clean);
    return false;
  }

  // Set bypass from URL
  const bypassSecret = params.get('bypass_maintenance');
  if (bypassSecret) {
    localStorage.setItem(BYPASS_STORAGE_KEY, bypassSecret);
    // Clean URL so the secret doesn't stay visible
    params.delete('bypass_maintenance');
    const clean = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}${window.location.hash}`;
    window.history.replaceState(null, '', clean);
    return true;
  }

  // Check persisted bypass
  return !!localStorage.getItem(BYPASS_STORAGE_KEY);
};

const useMaintenanceMode = () => {
  const [bypassed] = useState<boolean>(isAdminBypassed);
  const [isMaintenance, setIsMaintenance] = useState<boolean>(() => {
    if (bypassed) return false;
    const local = localStorage.getItem('scrudix_maintenance');
    if (local === 'true') return true;
    if (local === 'false') return false;
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MAINTENANCE_MODE === 'true') return true;
    return false;
  });

  const [config, setConfig] = useState<{ message?: string; estimatedMinutes?: number }>({
    message: undefined,
    estimatedMinutes: 30,
  });

  useEffect(() => {
    if (bypassed) {
      setIsMaintenance(false);
      return;
    }

    const checkBackend = async () => {
      try {
        const res = await fetch('/api/maintenance/status');
        if (res.ok) {
          const data = await res.json();
          if (typeof data.maintenance === 'boolean') {
            setIsMaintenance(data.maintenance);
            setConfig({
              message: data.message || undefined,
              estimatedMinutes: data.estimatedMinutes ?? 30,
            });
          }
        }
      } catch {
        // If backend is unreachable, keep current state
      }
    };

    void checkBackend();
    const interval = setInterval(checkBackend, 30_000);
    return () => clearInterval(interval);
  }, [bypassed]);

  return { isMaintenance, config, bypassed };
};

/** Floating admin banner shown when you bypass maintenance */
const AdminBypassBanner: React.FC<{ onDisableMaintenance: () => void }> = ({ onDisableMaintenance }) => (
  <div
    style={{
      position: 'fixed',
      bottom: 16,
      right: 16,
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 16px',
      borderRadius: 12,
      background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 700,
      boxShadow: '0 4px 24px rgba(124, 58, 237, 0.4)',
      fontFamily: 'system-ui, sans-serif',
      letterSpacing: '0.03em',
      userSelect: 'none',
      animation: 'maintContentEnter 0.4s ease both',
    }}
  >
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', display: 'inline-block', animation: 'maintDotPulse 1.5s ease-in-out infinite' }} />
      MAINTENANCE ACTIVE — Admin Bypass
    </span>
    <button
      onClick={onDisableMaintenance}
      style={{
        padding: '3px 10px',
        borderRadius: 6,
        background: 'rgba(255,255,255,0.2)',
        border: '1px solid rgba(255,255,255,0.3)',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
      }}
      title="Turn off maintenance mode via API"
    >
      Disable Maintenance
    </button>
    <button
      onClick={() => {
        localStorage.removeItem(BYPASS_STORAGE_KEY);
        window.location.reload();
      }}
      style={{
        padding: '3px 10px',
        borderRadius: 6,
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.2)',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
      }}
      title="Remove bypass and see the maintenance page"
    >
      See Maintenance
    </button>
  </div>
);

const App: React.FC = () => {
  const { isMaintenance, config: maintenanceConfig, bypassed: isAdminBypass } = useMaintenanceMode();
  const { isDark } = useTheme();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const currentView = resolveView(location.pathname);

  // ── Zustand auth store (replaces local useState + localStorage) ──
  const userData = useAuthStore((s) => s.user);
  const isHydrating = useAuthStore((s) => s.isHydrating);
  const authLogin = useAuthStore((s) => s.login);
  const authLogout = useAuthStore((s) => s.logout);
  const hydrateSession = useAuthStore((s) => s.hydrateSession);
  const hideHeader = useUIStore((s) => s.hideHeader);

  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isMascotVideoLoaded, setIsMascotVideoLoaded] = useState(true);
  const [blogPostSlug, setBlogPostSlug] = useState<string>(() => getBlogSlug(location.pathname) ?? '');
  const [comparisonSlug, setComparisonSlug] = useState<string>(() => getComparisonSlug(location.pathname) ?? '');
  const currentYear = new Date().getFullYear();

  // Track a recent login so we don't immediately re-verify a freshly-minted session.
  // Persist the grace timestamp in sessionStorage so it survives a full page reload
  // right after login (OAuth redirects, email verification, etc.).
  const FRESH_LOGIN_GRACE_KEY = 'scrudix_fresh_login_until';
  const freshLoginUntilRef = useRef<number>(readFreshLoginGrace(FRESH_LOGIN_GRACE_KEY));

  // Legacy hash redirect — handles old bookmarks like /#/dashboard → /dashboard
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#/')) {
      const path = hash.slice(1); // '#/dashboard' → '/dashboard'
      routerNavigate(path === '/' ? '/' : path, { replace: true });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const exitTimer = window.setTimeout(() => {
      setIsExiting(true);
    }, 1800);

    const loadingTimer = window.setTimeout(() => {
      setIsAppLoading(false);
    }, 2300);

    const hydrateAuth = async () => {
      try {
        const url = new URL(window.location.href);
        const oauthStatus = url.searchParams.get('oauth');
        const oauthToken = url.searchParams.get('token');
        const oauthRefreshToken = url.searchParams.get('refreshToken');
        const oauthMessage = url.searchParams.get('message');

        const oauthProvider = url.searchParams.get('provider');

        if (oauthStatus === 'success' && oauthToken) {
          localStorage.setItem('scrudix_token', oauthToken);
          if (oauthRefreshToken) {
            localStorage.setItem('scrudix_refresh_token', oauthRefreshToken);
          }
          localStorage.setItem('scrudix_auth', 'true');

          if (oauthProvider) {
            localStorage.setItem('scrudix_oauth_just_connected', oauthProvider);
            localStorage.setItem(`scrudix_${oauthProvider}_connected`, 'true');
          }

          // Keep OAuth auth flow consistent with email/password: show workspace chooser once.
          if (oauthProvider === 'google' || oauthProvider === 'github') {
            localStorage.setItem('scrudix_workspace_choice_required', 'true');
          }

          sessionStorage.setItem('scrudix_oauth_redirect_pending', oauthProvider || 'other');
        }

        if (oauthStatus === 'error' && oauthMessage) {
          sessionStorage.setItem('scrudix_oauth_error', oauthMessage);
          sessionStorage.setItem('scrudix_oauth_redirect_pending', 'error');
        }

        if (oauthStatus || oauthToken || oauthRefreshToken || oauthMessage) {
          url.searchParams.delete('oauth');
          url.searchParams.delete('token');
          url.searchParams.delete('refreshToken');
          url.searchParams.delete('provider');
          url.searchParams.delete('message');
          window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
        }

        // Delegate the actual token verification to Zustand store
        await hydrateSession();

        const pendingRedirect = sessionStorage.getItem('scrudix_oauth_redirect_pending');
        if (pendingRedirect) {
          sessionStorage.removeItem('scrudix_oauth_redirect_pending');
          if (pendingRedirect === 'google' || pendingRedirect === 'github' || pendingRedirect === 'error') {
            routerNavigate(viewToPath[View.AUTH], { replace: true });
          } else {
            routerNavigate(viewToPath[View.DASHBOARD], { replace: true });
          }
        }

        if (!cancelled) {
          const storeState = useAuthStore.getState();

          const authOnlyView = currentView === View.DASHBOARD || currentView === View.HIGGSFIELD;
          if (!storeState.isAuthenticated && authOnlyView) {
            routerNavigate(viewToPath[View.AUTH], { replace: true });
          }
        }
      } catch (error) {
        console.error('Session initialization failed:', error);
        // Don't logout on network errors — only on auth rejections
        // The Zustand hydrateSession already handles the distinction
      }
    };

    void hydrateAuth();

    return () => {
      cancelled = true;
      window.clearTimeout(exitTimer);
      window.clearTimeout(loadingTimer);
    };
  }, []);

  // Global rate-limit toast — fires when apiClient dispatches 'scrudix:rate-limited'
  useEffect(() => {
    const onRateLimited = (e: Event) => {
      const { retryAfterSeconds } = (e as CustomEvent).detail ?? {};
      toast.warning(`Rate limited — retry in ${retryAfterSeconds ?? 30}s`, {
        id: 'rate-limit',         // dedupe concurrent 429s into one toast
        duration: (retryAfterSeconds ?? 30) * 1000,
      });
    };
    window.addEventListener('scrudix:rate-limited', onRateLimited);
    return () => window.removeEventListener('scrudix:rate-limited', onRateLimited);
  }, []);

  // Capture referral parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('u');
    if (ref) {
      localStorage.setItem('scrudix_referred_by', ref);
      console.log('Saved referral code:', ref);
    }
  }, []);

  // Global auth invalidation handler — ensures stale sessions are fully cleared in UI state.
  useEffect(() => {
    const onAuthInvalidated = (e: Event) => {
      // Only act when the user is on a page that requires authentication.
      // Public pages (homepage, pricing, features, etc.) should stay silent
      // so Googlebot and new visitors never get pushed to /auth or see "session expired".
      const protectedViews = new Set<View>([View.DASHBOARD, View.HIGGSFIELD, View.STUDIO]);
      if (!protectedViews.has(currentView)) {
        authLogout();
        return;
      }

      const message = (e as CustomEvent<{ message?: string }>).detail?.message || 'Session expired. Please sign in again.';
      authLogout();
      toast.error(message, {
        id: 'auth-invalidated',
        duration: 4500,
      });
      routerNavigate(viewToPath[View.AUTH], { replace: true });
    };

    window.addEventListener('scrudix:auth-invalidated', onAuthInvalidated);
    return () => window.removeEventListener('scrudix:auth-invalidated', onAuthInvalidated);
  }, [authLogout, routerNavigate, currentView]);

  const handleLoginSuccess = (user: UserData, freshToken?: string) => {
    const token = freshToken ?? useAuthStore.getState().token ?? localStorage.getItem('scrudix_token');
    if (!token) {
      console.error('No auth token available after login; cannot navigate to dashboard.');
      return;
    }
    authLogin(user, token);
    localStorage.removeItem('scrudix_workspace_choice_required');

    // Post referral to backend if active
    const referredBy = localStorage.getItem('scrudix_referred_by');
    if (referredBy) {
      apiClient.post('/billing/referrals/register', { referrerEmail: referredBy })
        .then((res: any) => {
          if (res.success) {
            localStorage.removeItem('scrudix_referred_by');
            console.log('Referral registered successfully!');
          }
        })
        .catch(err => console.error('Failed to register referral:', err));
    }

    // Treat this session as freshly-minted for a few seconds so the dashboard
    // doesn't immediately re-verify it and risk bouncing the user back to /auth
    // if a stale 401 races the new token.
    const until = Date.now() + 30000;
    freshLoginUntilRef.current = until;
    try {
      sessionStorage.setItem(FRESH_LOGIN_GRACE_KEY, String(until));
    } catch {
      // Ignore storage errors (private browsing, etc.)
    }
    trackEvent('auth_success', { source: 'auth_form' });
    const path = window.location.pathname;
    const search = window.location.search;
    if (path && path !== '/auth' && path !== '/') {
      routerNavigate(`${path}${search}`);
    } else {
      routerNavigate(viewToPath[View.DASHBOARD]);
    }
  };

  const handleLogout = () => {
    trackEvent('logout', { source: 'dashboard' });
    authLogout();
    routerNavigate(viewToPath[View.LANDING]);
  };

  const secureView = useMemo(() => {
    const protectedView = currentView === View.DASHBOARD || currentView === View.HIGGSFIELD;
    // While we are still hydrating the session from localStorage/backend, don't
    // flash the /auth form on protected routes. Wait for hydration to finish so
    // a valid token can keep the user on the dashboard.
    if (protectedView && !userData) {
      return isHydrating ? currentView : View.AUTH;
    }
    return currentView;
  }, [currentView, userData, isHydrating]);

  const navigateToView = useCallback((nextView: View, source = 'navigation', slug?: string) => {
    trackEvent('navigate', { from: secureView, to: nextView, source });
    if (nextView === View.BLOG_POST && slug) {
      setBlogPostSlug(slug);
      routerNavigate(`/blog/${slug}`);
    } else if (nextView === View.COMPARISON && slug) {
      setComparisonSlug(slug);
      routerNavigate(`/${slug}`);
    } else if (nextView === View.BLOG_POST && !slug) {
      routerNavigate(viewToPath[View.BLOG]);
    } else if (nextView === View.COMPARISON && !slug) {
      routerNavigate(viewToPath[View.BLOG]);
    } else {
      routerNavigate(viewToPath[nextView]);
    }
  }, [secureView, routerNavigate]);

  // React Router handles browser back/forward automatically

  useEffect(() => {
    const handlePublishBlueprint = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        localStorage.setItem('scrudix_pending_blueprint', JSON.stringify(customEvent.detail));
        navigateToView(View.COMMUNITY, 'studio_publish');
      }
    };
    window.addEventListener('scrudix_publish_blueprint', handlePublishBlueprint);
    return () => {
      window.removeEventListener('scrudix_publish_blueprint', handlePublishBlueprint);
    };
  }, [navigateToView]);

  useEffect(() => {
    let cancelled = false;

    const verifyDashboardSession = async () => {
      if (secureView !== View.DASHBOARD) return;
      // Wait for initial hydration to finish before verifying. Hydration already
      // calls /auth/me; doing it again while it is in flight can cause a race.
      if (isHydrating) return;
      // If we just logged in, trust the fresh token instead of immediately
      // re-verifying it. This prevents a stale 401 from bouncing the user
      // back to /auth right after a successful login.
      if (Date.now() < freshLoginUntilRef.current) return;

      // Try up to 2 times on network errors so one dropped packet doesn't log
      // a legitimate user out of the dashboard.
      let lastError: string | undefined;
      for (let attempt = 0; attempt < 2; attempt++) {
        const response = await apiClient.getCurrentUser();
        if (cancelled) return;

        if (response.success && response.data?.user) {
          useAuthStore.getState().setUser(response.data.user as UserData);
          return;
        }

        lastError = response.error;
        const isTransientError =
          response.error?.includes('Cannot reach backend') ||
          response.error?.includes('Network error') ||
          response.error?.includes('Failed to fetch') ||
          response.error?.includes('temporarily unavailable') ||
          response.error?.includes('try again');

        // Only retry on transient errors; auth rejection is final.
        if (!isTransientError) break;
        if (attempt === 0) {
          await new Promise((resolve) => window.setTimeout(resolve, 800));
          if (cancelled) return;
        }
      }

      // Only log out on actual auth rejection — NOT on transient errors
      // (e.g. backend temporarily unreachable shouldn't nuke a valid session)
      // Treat 5xx / "temporarily unavailable" as transient.
      const isTransientError =
        lastError?.includes('Cannot reach backend') ||
        lastError?.includes('Network error') ||
        lastError?.includes('Failed to fetch') ||
        lastError?.includes('temporarily unavailable') ||
        lastError?.includes('try again');
      if (!isTransientError) {
        authLogout();
        routerNavigate(viewToPath[View.AUTH], { replace: true });
      }
    };

    void verifyDashboardSession();

    return () => {
      cancelled = true;
    };
  }, [secureView, isHydrating, authLogout, routerNavigate]);

  useEffect(() => {
    const blogSlug = getBlogSlug(location.pathname);
    if (blogSlug) setBlogPostSlug(blogSlug);

    const compSlug = getComparisonSlug(location.pathname);
    if (compSlug) setComparisonSlug(compSlug);

    let meta = pageMetaByView[secureView] ?? defaultPageMeta;

    if (secureView === View.COMPARISON && compSlug) {
      const comparisonPage = getComparisonPage(compSlug);
      if (comparisonPage) {
        meta = {
          title: comparisonPage.title,
          description: comparisonPage.metaDescription,
          keywords: comparisonPage.keywords.join(', '),
        };
      }
    }

    const indexable = secureView !== View.AUTH && secureView !== View.DASHBOARD && secureView !== View.NOT_FOUND;
    applyPageMeta(meta, { indexable });
    trackPageView(secureView);
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [secureView, location.pathname]);

  // Lenis Ultra-Smooth Physics Scrolling Engine
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Only run Lenis on regular document pages, not in full-screen application views
    // where internal nested containers handle their own native scrolling.
    const isAppWorkspace =
      secureView === View.STUDIO ||
      secureView === View.LUMI ||
      secureView === View.DASHBOARD ||
      secureView === View.HIGGSFIELD;

    if (isAppWorkspace) {
      return;
    }

    const lenis = new Lenis({
      duration: 0.9,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.2,
    });

    let animationFrameId: number;
    const raf = (time: number) => {
      lenis.raf(time);
      animationFrameId = requestAnimationFrame(raf);
    };
    animationFrameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(animationFrameId);
      lenis.destroy();
    };
  }, [secureView]);

  useEffect(() => {
    if (!userData) return;

    void syncAnalyticsWithBackend();
    const intervalId = window.setInterval(() => {
      void syncAnalyticsWithBackend();
    }, 12000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncAnalyticsWithBackend();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userData]);

  const showHeader =
    secureView !== View.DASHBOARD &&
    secureView !== View.TEAM_DASHBOARD &&
    secureView !== View.DOCUMENTATION &&
    secureView !== View.AUTH &&
    secureView !== View.HIGGSFIELD &&
    secureView !== View.STUDIO &&
    secureView !== View.LUMI;

  const showFooter =
    secureView !== View.DASHBOARD &&
    secureView !== View.TEAM_DASHBOARD &&
    secureView !== View.DOCUMENTATION &&
    secureView !== View.AUTH &&
    secureView !== View.LUMI &&
    secureView !== View.FEATURES &&
    secureView !== View.HOW_IT_WORKS &&
    secureView !== View.HIGGSFIELD &&
    secureView !== View.STUDIO;

  // ── Maintenance gate — show before anything else ──
  if (isMaintenance) {
    return (
      <MaintenancePage
        message={maintenanceConfig.message}
        estimatedMinutes={maintenanceConfig.estimatedMinutes}
        showCountdown
      />
    );
  }

  // Helper to disable maintenance via API from the admin banner
  const handleDisableMaintenance = async () => {
    const secret = localStorage.getItem(BYPASS_STORAGE_KEY) || 'scrudix-admin-secret';
    try {
      await fetch('/api/maintenance/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-secret': secret },
        body: JSON.stringify({ maintenance: false }),
      });
      localStorage.removeItem(BYPASS_STORAGE_KEY);
      window.location.reload();
    } catch {
      alert('Failed to disable maintenance. Check your admin secret.');
    }
  };

  const renderView = () => {
    switch (secureView) {
      case View.LANDING:
        return <Hero onNavigate={(view) => navigateToView(view, 'hero')} isAuthenticated={Boolean(userData)} />;
      case View.LUMI:
        return (
          <PanelErrorBoundary panelName="Lumi">
            <LumiExperience />
          </PanelErrorBoundary>
        );
      case View.GENERATOR:
        return <AIBuilder onNavigate={(view) => navigateToView(view, 'generator')} isAuthenticated={Boolean(userData)} />;
      case View.DEMO:
        return (
          <DemoShowcase
            onNavigate={(view) => navigateToView(view, 'demo')}
            onClose={() => navigateToView(View.LANDING, 'demo_close')}
            isAuthenticated={Boolean(userData)}
          />
        );
      case View.DASHBOARD:
        return (
          <Dashboard
            onBack={() => navigateToView(View.LANDING, 'dashboard_back')}
            onLogout={handleLogout}
            userData={userData}
          />
        );
      case View.PRICING:
        return <Pricing onNavigate={(view) => navigateToView(view, 'pricing')} isAuthenticated={Boolean(userData)} />;
      case View.EXTENSIONS:
        return <ExtensionsPage onNavigate={(view) => navigateToView(view, 'extensions')} />;
      case View.DOCUMENTATION:
        return <Documentation onNavigate={(view) => navigateToView(view, 'documentation')} />;
      case View.PRIVACY:
      case View.TERMS:
      case View.LEGAL:
      case View.CONTACT:
        return <LegalPages page={secureView} onNavigate={(view) => navigateToView(view, 'legal_pages')} />;
      case View.COMMANDS:
        return <CommandsPage onNavigate={(view: View) => navigateToView(view, 'commands')} />;
      case View.FEATURES:
        return <FeaturesPage onNavigate={(view) => navigateToView(view, 'features')} />;
      case View.HOW_IT_WORKS:
        return <HowItWorksPage onNavigate={(view) => navigateToView(view, 'how-it-works')} />;
      case View.HIGGSFIELD:
        return <HiggsfieldExperience onNavigate={(view: View) => navigateToView(view, 'higgsfield')} />;
      case View.WORKCLOCK:
        return <WorkClockDevPage />;
      case View.TEAM_DASHBOARD:
        return <TeamDashboardPage />;
      case View.STUDIO:
        return <ScrudixStudioAIStudio isDark={isDark} />;
      case View.NOT_FOUND:
        return <NotFoundPage onNavigate={navigateToView} />;
      case View.BLOG:
        return <BlogPage onNavigate={(view, slug) => navigateToView(view, 'blog', slug)} />;
      case View.BLOG_POST:
        return <BlogPost slug={blogPostSlug} onNavigate={(view, slug) => navigateToView(view, 'blog_post', slug)} />;
      case View.COMPARISON:
        return <ComparisonPage slug={comparisonSlug} onNavigate={(view, slug) => navigateToView(view, 'comparison', slug)} />;
      case View.COMMUNITY:
        return <CommunityPage onNavigate={(view) => navigateToView(view, 'community')} />;
      case View.LEETCODE:
        return (
          <div className="min-h-screen bg-slate-950 text-white">
            <LeetCodeTutorArena
              initialFocusMode={true}
              onBackToChat={() => navigateToView(View.LUMI, 'leetcode_back')}
            />
          </div>
        );
      case View.AUTH:
      default:
        return <Auth onLoginSuccess={handleLoginSuccess} />;
    }
  };

  if (isAppLoading) {
    return (
      <div className={`scrudix-splash${isExiting ? ' scrudix-splash-exit' : ''}`}>
        <div className="scrudix-splash-ambient-glow" />
        <div className="scrudix-splash-content">
          <div className="scrudix-mascot-track">
            <div className="scrudix-mascot-runner">
              <TransparentMascotCharacter
                onLoadedData={() => setIsMascotVideoLoaded(true)}
                onCanPlay={() => setIsMascotVideoLoaded(true)}
                className={`scrudix-mascot-video ${isMascotVideoLoaded ? 'is-loaded' : ''}`}
              />
              <div className="scrudix-mascot-shadow" />
              <div className="scrudix-loading-dots-wrapper" aria-label="Loading">
                <span className="scrudix-loading-dot" style={{ animationDelay: '0ms' }} />
                <span className="scrudix-loading-dot" style={{ animationDelay: '140ms' }} />
                <span className="scrudix-loading-dot" style={{ animationDelay: '280ms' }} />
                <span className="scrudix-loading-dot" style={{ animationDelay: '420ms' }} />
                <span className="scrudix-loading-dot" style={{ animationDelay: '560ms' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary onReset={() => navigateToView(View.LANDING, 'error_recovery')}>
      <Toaster 
        position="top-right" 
        closeButton 
        expand
        toastOptions={{
          className: 'scrudix-toast border border-zinc-900/10 bg-white/95 dark:bg-zinc-950/90 text-zinc-900 dark:text-zinc-50 backdrop-blur-xl shadow-2xl rounded-2xl font-semibold'
        }}
      />
      <ReminderBanner />
      <ReminderAlerts />
      <div className="relative isolate min-h-screen selection:bg-black selection:text-white dark:selection:bg-white dark:selection:text-black">
        <ThemeVideoBackdrop enabled={isDark} />
        <div className="relative z-10 min-h-screen">
          {/* Admin bypass banner — only visible when you're bypassing maintenance */}
          {isAdminBypass && <AdminBypassBanner onDisableMaintenance={handleDisableMaintenance} />}
          <a
            href="#main-content"
            className="sr-only z-[100] focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:rounded-xl focus:bg-zinc-900 focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white"
          >
            Skip to content
          </a>

          {showHeader && (
            <Header
              onNavigate={(view) => navigateToView(view, 'header')}
              currentView={secureView}
              isAuthenticated={Boolean(userData)}
              onDashboard={() => navigateToView(userData ? View.DASHBOARD : View.AUTH, 'header_dashboard')}
              hidden={hideHeader}
            />
          )}

          <main id="main-content">
            <Suspense fallback={<RouteLoadingFallback />}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={secureView}
                  initial={{ opacity: 0, y: 16, scale: 0.985 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.99 }}
                  transition={{
                    duration: 0.35,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="page-transition relative z-10 pointer-events-auto"
                >
                  {renderView()}
                </motion.div>
              </AnimatePresence>
            </Suspense>

            {showFooter && (
              <footer className="relative px-4 sm:px-6 pb-12 sm:pb-16 pt-10 sm:pt-14 md:px-10 md:pt-16">
                <div className="mx-auto max-w-6xl rounded-2xl sm:rounded-[2.2rem] border border-white/50 dark:border-white/10 bg-white/20 dark:bg-zinc-900/20 backdrop-blur-xl p-5 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.04)] md:p-10">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <button
                      className="inline-flex items-center gap-3 self-start text-left"
                      aria-label="Go to Scrudix home"
                      onClick={() => navigateToView(View.LANDING, 'footer_logo')}
                    >
                      <ScrudixLogo size="md" />
                      <span className="text-2xl font-black tracking-tight text-zinc-900 dark:text-white">Scrudix</span>
                    </button>
                    <p className="max-w-xl text-sm font-medium text-zinc-600 dark:text-zinc-300 md:text-right">
                      Build AI products with one system for interface, backend logic, docs, and analysis workflows.
                    </p>
                  </div>

                  <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {footerInfoCards.map((card) => (
                      <div key={card.title} className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/30 dark:bg-white/5 p-4 backdrop-blur-md hover:bg-white/50 dark:hover:bg-white/10 transition-all shadow-xs">
                        <p className="text-sm font-black uppercase tracking-[0.18em] text-zinc-800 dark:text-zinc-200">{card.title}</p>
                        <p className="mt-3 min-h-[72px] text-sm font-medium leading-relaxed text-zinc-600 dark:text-zinc-400">{card.details}</p>
                        <button
                          aria-label={card.actionLabel}
                          onClick={() => {
                            trackEvent('footer_card_click', { card: card.title, target: card.view });
                            navigateToView(card.view, 'footer_card');
                          }}
                          className="mt-4 inline-flex text-sm font-bold text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-900 dark:text-white dark:decoration-zinc-600 dark:hover:decoration-white"
                        >
                          {card.actionLabel}
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col gap-4 border-t border-zinc-200/80 dark:border-zinc-700/80 pt-6 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap items-center gap-5 text-sm font-semibold text-zinc-600 dark:text-zinc-400">
                      <a href="https://x.com/scrudix" target="_blank" rel="noreferrer" className="hover:text-zinc-900 dark:hover:text-white">
                        X
                      </a>
                      <a href="https://www.instagram.com/scrudix.ai/" target="_blank" rel="noreferrer" className="hover:text-zinc-900 dark:hover:text-white">
                        Instagram
                      </a>
                      <a href="https://github.com/scrudix" target="_blank" rel="noreferrer" className="hover:text-zinc-900 dark:hover:text-white">
                        GitHub
                      </a>
                      <a href="/blog" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'blog' }); navigateToView(View.BLOG, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Blog
                      </a>
                       <a href="/privacy" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'privacy' }); navigateToView(View.PRIVACY, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Privacy
                      </a>
                      <a href="/terms" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'terms' }); navigateToView(View.TERMS, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Terms
                      </a>
                      <a href="/legal" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'legal' }); navigateToView(View.LEGAL, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Legal
                      </a>
                      <a href="/contact" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'contact' }); navigateToView(View.CONTACT, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Contact
                      </a>
                      <a href="/studio" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'studio' }); navigateToView(View.STUDIO, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Studio
                      </a>
                      <a href="/extensions" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'extensions' }); navigateToView(View.EXTENSIONS, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Extensions
                      </a>
                      <a href="/commands" onClick={(e) => { e.preventDefault(); trackEvent('footer_link_click', { target: 'commands' }); navigateToView(View.COMMANDS, 'footer_link'); }} className="hover:text-zinc-900 dark:hover:text-white">
                        Commands
                      </a>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-zinc-400 dark:text-zinc-500">
                      (c) {currentYear} Scrudix. Speed of thought.
                    </p>
                  </div>
                </div>
              </footer>
            )}
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default App;
